import React, { useContext, useState } from "react";
import MediaProductTypeDropdown from "./components/MediaProductTypeDropdown";
import usePostGenerator from "utils/hooks/usePostGenerator";
import MoreOptions from "./components/MoreOptions";
import YoutubeVideoPreview from "./YoutubeVideoPreview";
import YoutubeShortPreview from "./YoutubeShortPreview";
import PlaylistDropdown from "./components/PlaylistDropdown";
import CategoryDropdown from "./components/CategoryDropdown";
import YoutubePrivacyLevelDropdown from "./components/YoutubePrivacyLevelDropdown";
import TagsInput from "./components/TagsInput";
import { CompanyContext } from "contexts/CompanyContext";
import { TbChevronDown, TbScreenshot } from "react-icons/tb";
import { Tooltip } from "components/ui";

const YoutubePreview = (props) => {

    const {
        isEditable,
        onDeleteNetworkPost,
        onCreateNetworkPost,
        onPreferencesEdit,
        allPosts,
        postIndex,
        isSimpleView,
        onCoverEdit,
        preferences,
    } = props;

    const network = "youtube";
    const { companySocials } = useContext(CompanyContext);
    const { generatePostWithNewMediaProductType } = usePostGenerator();
    const [ showConfig, setShowConfig ] = useState(false);
    const integration = companySocials?.filter(e => e.type == network)?.[0];


    const handleOnMediaProductTypeChange = (media_product_type) => {
        const postToCopy = allPosts?.[postIndex];

        const newPost = generatePostWithNewMediaProductType(postToCopy, network, media_product_type);
        !!onCreateNetworkPost && onCreateNetworkPost({...newPost}, true);
    }

    // In readonly mode only show settings that are actually set — empty
    // "Select..." pickers are edit affordances, not post content.
    const hasReadonlyConfig = !!preferences?.privacy_level || !!preferences?.tags?.length || !!preferences?.category_id || !!preferences?.playlist_id;
    
    return (<>
        {!isSimpleView && (
            <div className="flex flex-col w-full">
                <div className="flex items-stretch justify-between w-full">
                    <div className="flex items-stretch w-full">
                        <MediaProductTypeDropdown 
                            post={props?.[0] || props}
                            isEditable={isEditable}
                            allowedMediaProductTypes={["FEED", "SHORT"]}
                            onChange={(media_product_type) => handleOnMediaProductTypeChange(media_product_type)}
                        />
                    </div>
                    {!!integration && (isEditable || hasReadonlyConfig) && (
                        <div
                            onClick={() => setShowConfig(!showConfig)}
                            className={`transition-colors flex items-center justify-center p-2 px-4 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700 border-l border-b border-gray-100 dark:border-gray-700 ${
                                showConfig 
                                    ? 'bg-primary-100/20 text-primary-600 dark:bg-primary-900/20 dark:text-primary-400' 
                                    : 'hover:bg-tertiary-100 text-tertiary-600 dark:hover:bg-gray-800 dark:text-gray-400'
                            }`}
                            title="Advanced settings"
                        >
                            <TbChevronDown className={`w-5 h-5 transition-transform ${showConfig ? 'rotate-180' : ''}`} />
                        </div>
                    )}
                </div>

                <div className={`transition-all duration-300 ${
                    showConfig ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'
                }`}>
                    <div className="flex flex-col ">
                        {(isEditable || !!preferences?.privacy_level) && (
                            <YoutubePrivacyLevelDropdown
                                post={props?.[0] || props}
                                isEditable={isEditable}
                                onChange={(privacy_level) => onPreferencesEdit(postIndex, { ...(props?.preferences || {}), privacy_level })}
                            />
                        )}
                        {(isEditable || !!preferences?.tags?.length) && (
                            <Tooltip
                                title="Press Enter after each tag to add it to your YouTube video"
                                wrapperClass="!w-full flex-1 flex justify-stretch"
                            >
                                <div className="w-full">
                                    <TagsInput
                                        post={props?.[0] || props}
                                        isEditable={isEditable}
                                        placeholder="Add Youtube tags..."
                                        className="!py-4 !px-4 border-b border-gray-100 dark:border-gray-700"
                                        onChange={(tags) => {
                                            const post = allPosts?.[postIndex];
                                            onPreferencesEdit(postIndex, { ...post.preferences, tags });
                                        }}
                                    />
                                </div>
                            </Tooltip>
                        )}
                        <div className="flex items-stretch !divide-x divide-gray-100 dark:divide-gray-700">
                            {(isEditable || !!preferences?.category_id) && (
                                <CategoryDropdown
                                    post={props?.[0] || props}
                                    isEditable={isEditable}
                                    onChange={(category_id) => {
                                        const post = allPosts?.[postIndex];
                                        onPreferencesEdit(postIndex, { ...post.preferences, category_id });
                                    }}
                                />
                            )}
                            {(isEditable || !!preferences?.playlist_id) && (
                                <PlaylistDropdown
                                    post={props?.[0] || props}
                                    isEditable={isEditable}
                                    onChange={(playlist_id) => {
                                        const post = allPosts?.[postIndex];
                                        onPreferencesEdit(postIndex, { ...post.preferences, playlist_id });
                                    }}
                                />
                            )}
                            {!!props?.assets?.[0]?.video?.url && preferences?.media_product_type == "FEED" && (
                                <Tooltip 
                                    title="Customize your YouTube video thumbnail" 
                                    wrapperClass="flex justify-stretch"
                                >
                                    <MoreOptions
                                        onChange={(_preferences) => onPreferencesEdit(postIndex, _preferences)}
                                        onCoverEdit={(cover) => onCoverEdit(postIndex, cover)}
                                        post={props?.[0] || props}
                                        isEditable={isEditable}
                                        icon={<TbScreenshot className="text-primary-600 text-2xl shrink-0" />}
                                        forceShowVideoCover={true}
                                        renderClassName="!border-l-0"
                                    />
                                </Tooltip>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        )}
        <YoutubeVideoPreview {...props} />
        <YoutubeShortPreview {...props} />
    </>)
}

export default YoutubePreview;